import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  FiBriefcase, FiUsers, FiMessageSquare, FiSend,
  FiRadio, FiWifi, FiGlobe,
  FiDollarSign, FiCreditCard, FiAlertTriangle, FiTrendingDown,
} from 'react-icons/fi';
import {
  AreaChart, Area, BarChart, Bar, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { apiGet } from '@/lib/api';
import { Card, CardHeader, CardContent } from '@/shared/components/ui/Card';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { ConnectedUsersDrawer } from '../components/ConnectedUsersDrawer';
import { providerLabels } from '../constants';
import { MrrDrawer } from '../components/MrrDrawer';
import { ActiveSubscriptionsDrawer } from '../components/ActiveSubscriptionsDrawer';
import { FailedPaymentsDrawer } from '../components/FailedPaymentsDrawer';
import { ChurnDrawer } from '../components/ChurnDrawer';
import { ChannelsDrawer } from '../components/ChannelsDrawer';

// --- Types ---

interface DashboardMetrics {
  totalCompanies: number;
  totalUsers: number;
  totalConversations: number;
  totalMessages: number;
  totalChannels: number;
  connectedChannels: number;
  disconnectedChannels: number;
  connectedUsers: number;
  connectedClients: number;
  mrr: number;
  activeSubscriptions: number;
  failedSubscriptions: number;
  churnRate: number;
  topCompaniesByConversations: { companyId: string; companyName: string; count: number }[];
}

interface ChartsData {
  volumesChart: { period: string; conversations: number; inbound: number; outbound: number; ai: number }[];
  messagesByProvider: { provider: string; count: number }[];
  subscriptionGrowthChart: { period: string; amount: number }[];
  clientGrowthChart: { period: string; companies: number; users: number }[];
}

// --- Components ---

interface StatCardProps {
  label: string;
  value: string | number | null;
  icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  loading?: boolean;
  suffix?: string;
  onClick?: () => void;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, icon: Icon, loading, suffix, onClick }) => (
  <Card>
    <div
      onClick={onClick}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      style={{ cursor: onClick ? 'pointer' : 'default' }}
    >
      <CardHeader className="pb-2 py-3 px-4">
        <div className="flex items-center justify-between">
          <span className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>{label}</span>
          <Icon className="h-4 w-4" style={{ color: 'var(--theme-primary)' }} />
        </div>
      </CardHeader>
      <CardContent className="px-4 pb-4">
        {loading ? (
          <Skeleton width={60} height={28} />
        ) : (
          <p className="text-2xl font-bold" style={{ color: 'var(--theme-card-text)' }}>
            {typeof value === 'number' ? value.toLocaleString('pt-BR') : value ?? '0'}
            {suffix && <span className="text-sm font-normal ml-1" style={{ color: 'var(--theme-muted-text)' }}>{suffix}</span>}
          </p>
        )}
      </CardContent>
    </div>
  </Card>
);

const CHART_COLORS = ['#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#3b82f6', '#ec4899'];

const TOOLTIP_STYLE = {
  contentStyle: { backgroundColor: 'var(--theme-card-background)', border: '1px solid var(--theme-card-border)', borderRadius: 8, fontSize: 12, color: 'var(--theme-card-text)' },
  labelStyle: { color: 'var(--theme-muted-text)' },
  itemStyle: { color: 'var(--theme-card-text)' },
  cursor: { fill: 'var(--theme-card-border)', opacity: 0.3 },
};
const AXIS_TICK = { fontSize: 11, fill: 'var(--theme-muted-text)' };
const GRID_STROKE = 'var(--theme-card-border)';

const formatProviderLabel = (providerId: unknown) => {
  const key = String(providerId);
  return providerLabels[key] || key;
};

const periodLabels: Record<string, string> = { day: 'Hoje', week: 'Semana', month: 'Mes' };
const growthPeriodLabels: Record<string, string> = { month: 'Mes', year: 'Ano', all: 'Desde o inicio' };

const formatGrowthPeriodLabel = (period: string, selectedPeriod: string) => {
  if (selectedPeriod === 'all') {
    const d = new Date(period + '-01');
    const month = d.toLocaleString('pt-BR', { month: 'short' }).replace('.', '');
    const year = d.getFullYear().toString().slice(2);
    return `${month}/${year}`;
  }
  if (selectedPeriod === 'year') {
    const d = new Date(period + '-01');
    return d.toLocaleString('pt-BR', { month: 'short' }).replace('.', '');
  }
  const d = new Date(period);
  return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}`;
};

const formatPeriodLabel = (period: string, selectedPeriod: string) => {
  if (selectedPeriod === 'day') {
    return period.slice(11, 16);
  }
  const d = new Date(period);
  return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}`;
};

const formatCurrency = (value: number) =>
  value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

// --- Main ---

export const DashboardPage: React.FC = () => {
  const [chartPeriod, setChartPeriod] = useState<'day' | 'week' | 'month'>('week');
  const [growthPeriod, setGrowthPeriod] = useState<'month' | 'year' | 'all'>('month');
  const [showConnectedUsers, setShowConnectedUsers] = useState(false);
  const [showMrr, setShowMrr] = useState(false);
  const [showActiveSubs, setShowActiveSubs] = useState(false);
  const [showFailedPayments, setShowFailedPayments] = useState(false);
  const [showChurn, setShowChurn] = useState(false);
  const [showChannels, setShowChannels] = useState(false);

  const { data: metrics, isLoading } = useQuery({
    queryKey: ['admin-dashboard'],
    queryFn: () => apiGet<DashboardMetrics>('/dashboard'),
  });

  const { data: charts, isLoading: chartsLoading } = useQuery({
    queryKey: ['admin-dashboard-charts', chartPeriod],
    queryFn: () => apiGet<ChartsData>(`/dashboard/charts?period=${chartPeriod}`),
  });

  const { data: growthCharts, isLoading: growthLoading } = useQuery({
    queryKey: ['admin-dashboard-growth', growthPeriod],
    queryFn: () => apiGet<ChartsData>(`/dashboard/charts?growthPeriod=${growthPeriod}`),
  });

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-xl font-bold" style={{ color: 'var(--theme-card-text)' }}>Dashboard</h1>
        <p className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>Visao geral do seu app</p>
      </div>

      {/* Row 1: Overview */}
      <div>
        <h2 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--theme-muted-text)' }}>Visao Geral</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard label="Companies" value={metrics?.totalCompanies ?? null} icon={FiBriefcase} loading={isLoading} />
          <StatCard label="Usuarios" value={metrics?.totalUsers ?? null} icon={FiUsers} loading={isLoading} />
          <StatCard label="Conversas" value={metrics?.totalConversations ?? null} icon={FiMessageSquare} loading={isLoading} />
          <StatCard label="Mensagens" value={metrics?.totalMessages ?? null} icon={FiSend} loading={isLoading} />
        </div>
      </div>

      {/* Row 2: Channels & Online */}
      <div>
        <h2 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--theme-muted-text)' }}>Canais & Online</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard label="Total de canais" value={metrics?.totalChannels ?? null} icon={FiRadio} loading={isLoading} />
          <StatCard
            label="Conectados / Desconectados"
            value={metrics ? `${metrics.connectedChannels.toLocaleString('pt-BR')} / ${metrics.disconnectedChannels.toLocaleString('pt-BR')}` : null}
            icon={FiWifi}
            loading={isLoading}
            onClick={() => setShowChannels(true)}
          />
          <StatCard
            label="Usuarios online"
            value={metrics?.connectedUsers ?? null}
            icon={FiUsers}
            loading={isLoading}
            onClick={() => setShowConnectedUsers(true)}
          />
          <StatCard label="Clientes online" value={metrics?.connectedClients ?? null} icon={FiGlobe} loading={isLoading} />
        </div>
      </div>

      {/* Row 3: Financial */}
      <div>
        <h2 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--theme-muted-text)' }}>Financeiro</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard label="MRR" value={metrics ? formatCurrency(metrics.mrr) : null} icon={FiDollarSign} loading={isLoading} onClick={() => setShowMrr(true)} />
          <StatCard label="Assinaturas ativas" value={metrics?.activeSubscriptions ?? null} icon={FiCreditCard} loading={isLoading} onClick={() => setShowActiveSubs(true)} />
          <StatCard label="Pagamentos com falha" value={metrics?.failedSubscriptions ?? null} icon={FiAlertTriangle} loading={isLoading} onClick={() => setShowFailedPayments(true)} />
          <StatCard label="Churn rate" value={metrics?.churnRate ?? null} icon={FiTrendingDown} loading={isLoading} suffix="%" onClick={() => setShowChurn(true)} />
        </div>
      </div>

      {/* Period selector for charts */}
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium" style={{ color: 'var(--theme-muted-text)' }}>Periodo:</span>
        {(['day', 'week', 'month'] as const).map((p) => (
          <button
            key={p}
            onClick={() => setChartPeriod(p)}
            className="px-3 py-1.5 text-sm rounded-lg transition-colors"
            style={{
              backgroundColor: chartPeriod === p ? 'var(--theme-primary)' : 'var(--theme-card-background)',
              color: chartPeriod === p ? 'var(--theme-button-primary-text)' : 'var(--theme-card-text)',
              border: chartPeriod === p ? 'none' : '1px solid var(--theme-card-border)',
            }}
          >
            {periodLabels[p]}
          </button>
        ))}
      </div>

      {/* Volumes Chart (full width) */}
      <Card>
        <CardHeader className="pb-2 py-3 px-4">
          <h2 className="text-sm font-semibold" style={{ color: 'var(--theme-card-text)' }}>Volumes</h2>
        </CardHeader>
        <CardContent className="px-4 pb-4">
          {chartsLoading ? (
            <Skeleton height={300} variant="rectangular" />
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={charts?.volumesChart || []}>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
                <XAxis dataKey="period" tick={AXIS_TICK} tickFormatter={(v) => formatPeriodLabel(v, chartPeriod)} stroke={GRID_STROKE} />
                <YAxis tick={AXIS_TICK} stroke={GRID_STROKE} />
                <Tooltip {...TOOLTIP_STYLE} />
                <Legend wrapperStyle={{ fontSize: 12, color: 'var(--theme-card-text)' }} />
                <Area type="monotone" dataKey="conversations" name="Conversas" stroke="#8b5cf6" fill="#8b5cf6" fillOpacity={0.1} strokeWidth={2} />
                <Area type="monotone" dataKey="inbound" name="Msg. recebidas" stroke="#10b981" fill="#10b981" fillOpacity={0.1} strokeWidth={2} />
                <Area type="monotone" dataKey="outbound" name="Msg. enviadas" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.1} strokeWidth={2} />
                <Area type="monotone" dataKey="ai" name="Msg. por IA" stroke="#f59e0b" fill="#f59e0b" fillOpacity={0.1} strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Row: Mensagens por Canal (bars) + Top 5 Companies (table) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Mensagens por Canal */}
        <Card>
          <CardHeader className="pb-2 py-3 px-4">
            <h2 className="text-sm font-semibold" style={{ color: 'var(--theme-card-text)' }}>Mensagens por Canal</h2>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            {chartsLoading ? (
              <Skeleton height={250} variant="rectangular" />
            ) : charts?.messagesByProvider && charts.messagesByProvider.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={charts.messagesByProvider}>
                  <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
                  <XAxis dataKey="provider" tick={AXIS_TICK} tickFormatter={formatProviderLabel} stroke={GRID_STROKE} />
                  <YAxis tick={AXIS_TICK} stroke={GRID_STROKE} />
                  <Tooltip {...TOOLTIP_STYLE} labelFormatter={formatProviderLabel} />
                  <Bar dataKey="count" name="Mensagens" radius={[4, 4, 0, 0]}>
                    {charts.messagesByProvider.map((_, i) => (
                      <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[250px]">
                <p className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>Sem dados no periodo</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top 5 Companies (table) */}
        <Card>
          <CardHeader className="pb-2 py-3 px-4">
            <h2 className="text-sm font-semibold" style={{ color: 'var(--theme-card-text)' }}>Top 5 Companies por Conversas</h2>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            {isLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} height={20} />)}
              </div>
            ) : metrics?.topCompaniesByConversations && metrics.topCompaniesByConversations.length > 0 ? (
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--theme-card-border)' }}>
                    <th className="text-left py-2 text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--theme-muted-text)' }}>#</th>
                    <th className="text-left py-2 text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--theme-muted-text)' }}>Company</th>
                    <th className="text-right py-2 text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--theme-muted-text)' }}>Conversas</th>
                  </tr>
                </thead>
                <tbody>
                  {metrics.topCompaniesByConversations.map((company, i) => (
                    <tr key={company.companyId} className={i > 0 ? 'border-t' : ''} style={i > 0 ? { borderColor: 'var(--theme-card-border)' } : {}}>
                      <td className="py-2.5" style={{ color: 'var(--theme-muted-text)' }}>{i + 1}</td>
                      <td className="py-2.5 font-medium" style={{ color: 'var(--theme-card-text)' }}>{company.companyName}</td>
                      <td className="py-2.5 text-right tabular-nums font-semibold" style={{ color: 'var(--theme-primary)' }}>
                        {company.count.toLocaleString('pt-BR')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="flex items-center justify-center h-[250px]">
                <p className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>Sem dados</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Growth Section */}
      <div className="space-y-4">
        <div>
          <h2 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--theme-muted-text)' }}>Crescimento</h2>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium" style={{ color: 'var(--theme-muted-text)' }}>Periodo:</span>
            {(['month', 'year', 'all'] as const).map((p) => (
              <button
                key={p}
                onClick={() => setGrowthPeriod(p)}
                className="px-3 py-1.5 text-sm rounded-lg transition-colors"
                style={{
                  backgroundColor: growthPeriod === p ? 'var(--theme-primary)' : 'var(--theme-card-background)',
                  color: growthPeriod === p ? 'var(--theme-button-primary-text)' : 'var(--theme-card-text)',
                  border: growthPeriod === p ? 'none' : '1px solid var(--theme-card-border)',
                }}
              >
                {growthPeriodLabels[p]}
              </button>
            ))}
          </div>
        </div>

        {/* Subscription Growth Chart (R$) */}
      <Card>
        <CardHeader className="pb-2 py-3 px-4">
          <h2 className="text-sm font-semibold" style={{ color: 'var(--theme-card-text)' }}>Crescimento de Assinaturas (R$)</h2>
        </CardHeader>
        <CardContent className="px-4 pb-4">
          {growthLoading ? (
            <Skeleton height={300} variant="rectangular" />
          ) : growthCharts?.subscriptionGrowthChart && growthCharts.subscriptionGrowthChart.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={growthCharts.subscriptionGrowthChart}>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
                <XAxis dataKey="period" tick={AXIS_TICK} tickFormatter={(v) => formatGrowthPeriodLabel(v, growthPeriod)} stroke={GRID_STROKE} />
                <YAxis tick={AXIS_TICK} stroke={GRID_STROKE} tickFormatter={(v) => formatCurrency(v)} />
                <Tooltip {...TOOLTIP_STYLE} formatter={(value) => formatCurrency(Number(value))} />
                <Legend wrapperStyle={{ fontSize: 12, color: 'var(--theme-card-text)' }} />
                <Area type="monotone" dataKey="amount" name="Novas Assinaturas (R$)" stroke="#06b6d4" fill="#06b6d4" fillOpacity={0.1} strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[300px]">
              <p className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>Sem dados no periodo</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Client & User Growth Chart */}
      <Card>
        <CardHeader className="pb-2 py-3 px-4">
          <h2 className="text-sm font-semibold" style={{ color: 'var(--theme-card-text)' }}>Crescimento de Clientes & Usuarios</h2>
        </CardHeader>
        <CardContent className="px-4 pb-4">
          {growthLoading ? (
            <Skeleton height={300} variant="rectangular" />
          ) : growthCharts?.clientGrowthChart && growthCharts.clientGrowthChart.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={growthCharts.clientGrowthChart}>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
                <XAxis dataKey="period" tick={AXIS_TICK} tickFormatter={(v) => formatGrowthPeriodLabel(v, growthPeriod)} stroke={GRID_STROKE} />
                <YAxis tick={AXIS_TICK} stroke={GRID_STROKE} />
                <Tooltip {...TOOLTIP_STYLE} />
                <Legend wrapperStyle={{ fontSize: 12, color: 'var(--theme-card-text)' }} />
                <Area type="monotone" dataKey="companies" name="Empresas" stroke="#8b5cf6" fill="#8b5cf6" fillOpacity={0.1} strokeWidth={2} />
                <Area type="monotone" dataKey="users" name="Usuarios" stroke="#f59e0b" fill="#f59e0b" fillOpacity={0.1} strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[300px]">
              <p className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>Sem dados no periodo</p>
            </div>
          )}
        </CardContent>
      </Card>
      </div>

      <ConnectedUsersDrawer
        isOpen={showConnectedUsers}
        onClose={() => setShowConnectedUsers(false)}
      />

      <MrrDrawer isOpen={showMrr} onClose={() => setShowMrr(false)} />
      <ActiveSubscriptionsDrawer isOpen={showActiveSubs} onClose={() => setShowActiveSubs(false)} />
      <FailedPaymentsDrawer isOpen={showFailedPayments} onClose={() => setShowFailedPayments(false)} />
      <ChurnDrawer isOpen={showChurn} onClose={() => setShowChurn(false)} />
      <ChannelsDrawer isOpen={showChannels} onClose={() => setShowChannels(false)} />
    </div>
  );
};
